import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Button, Card, Stack, Typography } from '@prometheus/components'
import { useGuestSession } from '../../hooks/useGuestSession'
import useRouting from '../../hooks/useRouting'
import { guestTokenFromLocation } from '../../utils/guestSession'
import { guestLinkAction } from './guestLink'

/**
 * /guest#<key> — a guest coming back to their games.
 *
 * The key in the link is the identity. Redeeming it means handing it to the
 * server, which resolves the player it belongs to; from there the socket
 * reconnects as that player and the lobby shows their games again.
 */
export const GuestLinkScreen = () => {
  const location = useLocation()
  const token = guestTokenFromLocation(location)
  const { session, startGuest, ready } = useGuestSession()
  const { replace, push } = useRouting()
  const [claiming, setClaiming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  const action = guestLinkAction(token, session?.token)
  const shouldClaim = action === 'claim' || (action === 'confirm' && confirmed)

  const claim = useCallback(async () => {
    if (!token) {
      return
    }

    setClaiming(true)
    setError(null)

    try {
      await startGuest(token)
      replace('/games')()
    } catch (e) {
      setError((e as Error)?.message || 'Could not open that play link.')
    } finally {
      setClaiming(false)
    }
  }, [token, startGuest, replace])

  useEffect(() => {
    if (ready && shouldClaim && !claiming && !error) {
      claim()
    }
    // `claim` changes identity with the socket; the guards above keep this to
    // one attempt per link until the player asks to retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, shouldClaim])

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
      <Card borderColor={error || action === 'invalid' ? 'danger' : 'primary'} style={{ width: '100%', maxWidth: '480px' }}>
        <Stack space="sm">
          <Typography variant="h4" retro color="primary" align="center">PROMETHEUS</Typography>

          {action === 'invalid' && (
            <Box space="sm">
              <Stack space="sm">
                <Typography align="center" color="danger">That play link is missing its key, or the key is damaged.</Typography>
                <Typography variant="caption" align="center" color="text-muted">
                  Check you copied the whole link, or start a new guest session from the lobby.
                </Typography>
                <Button fullWidth variant="primary" outline onClick={push('/games')}>Back to Games</Button>
              </Stack>
            </Box>
          )}

          {action === 'confirm' && !confirmed && (
            <Box space="sm">
              <Stack space="sm">
                <Typography align="center">
                  You’re already playing as <strong>{session?.name || 'a guest'}</strong>.
                </Typography>
                <Typography variant="caption" align="center" color="text-muted">
                  Opening this link switches you to the guest it belongs to. Save your current
                  play link first if you want to come back to these games.
                </Typography>
                <Button fullWidth variant="warning" onClick={() => setConfirmed(true)}>Switch Players</Button>
                <Button fullWidth variant="secondary" outline onClick={push('/games')}>Stay as {session?.name || 'I am'}</Button>
              </Stack>
            </Box>
          )}

          {shouldClaim && !error && (
            <Typography align="center" retro>Opening your games…</Typography>
          )}

          {!!error && (
            <Box space="sm">
              <Stack space="sm">
                <Typography align="center" color="danger">{error}</Typography>
                <Button fullWidth variant="primary" loading={claiming} disabled={claiming} onClick={claim}>Try Again</Button>
                <Button fullWidth variant="secondary" outline onClick={push('/games')}>Back to Games</Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Card>
    </div>
  )
}
